import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, interval } from 'rxjs';
import { take } from 'rxjs/operators';
import { RouterOutlet } from '@angular/router';
import { LoaderService } from './core/services/loader/loader.service';

@Component({
    selector: 'app-root',
    imports: [RouterOutlet],
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {

  loader = inject(LoaderService);
  #destroyRef = inject(DestroyRef);

  title = 'mp-fe-project';

  private preheaderResizeObserver?: ResizeObserver;
  private preheaderMutationObserver?: MutationObserver;

  ngOnInit(): void {
    this.setupPreheaderStickyOffset();
  }

  private readonly syncPreheaderHeight = (): void => {
    if (document.body.classList.contains('mobile-nav-open')) {
      return;
    }

    const preHeader = document.querySelector('header.ecomic-toolbar-anchor .ecomic-pre-header');
    const anchor = document.querySelector('header.ecomic-toolbar-anchor');
    const measured = preHeader ?? anchor;
    if (!measured) {
      return;
    }

    const height = Math.round(measured.getBoundingClientRect().height);
    if (height <= 0) {
      return;
    }

    document.documentElement.style.setProperty('--ecomic-preheader-height', `${height}px`);
  };

  private setupPreheaderStickyOffset(): void {
    const anchor = document.querySelector('header.ecomic-toolbar-anchor');
    if (!anchor) {
      return;
    }

    this.syncPreheaderHeight();

    // toolbar.js inietta la pre-header in modo asincrono: ritenta finché non ha dimensioni reali
    interval(100)
      .pipe(take(100), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.syncPreheaderHeight());

    this.preheaderResizeObserver = new ResizeObserver(() => this.syncPreheaderHeight());
    this.preheaderResizeObserver.observe(anchor);

    this.preheaderMutationObserver = new MutationObserver(() => this.syncPreheaderHeight());
    this.preheaderMutationObserver.observe(anchor, { childList: true, subtree: true });

    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.syncPreheaderHeight());

    fromEvent(window, 'load')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.syncPreheaderHeight());

    this.#destroyRef.onDestroy(() => {
      this.preheaderResizeObserver?.disconnect();
      this.preheaderMutationObserver?.disconnect();
    });
  }
}
